/**
 * dcent-app-feature-demos.js — 앱 페이지 후반부 기능 섹션 화면의 연출 제어.
 *
 * 마크업은 `snippets/dcent-app-*-demo.liquid`, 외형은 `dcent-app-feature-demos.css`.
 * 원본: `20260827 homepage_전달용/src/components/` 의 같은 이름 컴포넌트들.
 *
 * 여기서 하는 일은 단계 표시(data-*)뿐이다 — 모양·전환은 전부 CSS 가 갖는다.
 * 앞서 만든 `dcent-app-swap-demos.js` 와 같은 뼈대이고, 소비처가 다른 섹션이라 파일만 나눴다.
 *
 * 안전 규약: 대상 없으면 조용히 종료 · 멱등 설치 · 보일 때만 재생 · 이탈 시 리셋.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */

if (!window.__dcentAppFeatureDemos) {
  window.__dcentAppFeatureDemos = true;

  const reduced = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /** 공통 재생기 — steps(ms)를 돌며 apply(root, i)로 단계를 화면에 반영한다. */
  const runner = (root, steps, apply) => {
    let i = 0;
    let timer = 0;
    const tick = () => {
      i = (i + 1) % steps.length;
      apply(root, i);
      timer = window.setTimeout(tick, steps[i]);
    };
    const start = () => {
      if (timer) return;
      timer = window.setTimeout(tick, steps[i]);
    };
    const reset = () => {
      window.clearTimeout(timer);
      timer = 0;
      i = 0;
      apply(root, 0);
    };

    apply(root, 0);

    if (!("IntersectionObserver" in window)) {
      start();
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? start() : reset()),
      { threshold: 0.15 },
    );
    io.observe(root);
  };

  /* ── GasPass ───────────────────────────────────────────────────────
   * 0 OFF · 1 탭 · 2 ON · 3 OFF 복귀 후 휴지. 한 바퀴 4.4초. */
  const GASPASS = [700, 250, 2350, 1100];

  const paintGasPass = (root, step) => {
    root.dataset.on = step === 2 ? "1" : "0";
    root.dataset.tapping = step === 1 ? "1" : "0";
  };

  const setupGasPass = (root) => {
    if (reduced()) {
      // ON 으로 정지 — 이 데모의 요지가 "켜면 수수료가 0" 이라 OFF 로 멈추면 할 말이 사라진다
      paintGasPass(root, 2);
      return;
    }
    runner(root, GASPASS, paintGasPass);
  };

  /* ── Volty (AI 어시스턴트) ─────────────────────────────────────────
   * 두 단계짜리 데모라 앞의 것들보다 상태가 많다.
   *   말풍선 점 로딩 → 말풍선 타이핑 → 배지 탭 → 시트 오픈
   *   → 질문 타이핑 → 답변 점 로딩 → 답변 스트리밍 → 칩 → 닫기 → 반복
   *
   * ⚠️ **서식이 섞인 글을 글자 단위로 흘리는 방법.** 답변에는 굵은 글씨·불릿이 섞여 있다.
   * 마크업을 글자로 쪼개면 태그가 깨지므로, 완성된 마크업은 그대로 두고 **텍스트 노드마다
   * 원문을 기억해 두었다가** 전역 카운터까지만 잘라 넣는다. 태그를 손대지 않으므로
   * 줄바꿈이 밀리지 않는다.
   */
  /* 타이핑 속도 두 벌 — 원본과 같다.
   *  · 말풍선(툴팁)은 한 글자씩 28ms — 짧은 문구라 또박또박 읽히는 게 요지다.
   *  · 시트 안 대화(질문·답변)는 **30ms 마다 8글자씩** 흘린다(≈260자/초, ChatGPT 류 스트림).
   * 종전엔 대화도 한 글자씩 28ms 라 답변 약 1,400자가 다 나오는 데 40초 가까이 걸렸다
   * (2026-08-28 QA "채팅이 너무 느리다"). 8글자/30ms 면 같은 답변이 약 5초다. */
  const VOLTY_TIP_MS = 28;
  const VOLTY_CHAT_MS = 30;
  const VOLTY_CHAT_CHARS = 8;

  /** 요소 안의 텍스트 노드를 모아 원문을 기억한다(마크업은 건드리지 않는다). */
  const collectText = (el) => {
    const out = [];
    const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let n;
    let total = 0;
    while ((n = walk.nextNode())) {
      const full = n.nodeValue;
      out.push({ node: n, full, start: total });
      total += full.length;
    }
    return { parts: out, total };
  };

  /** 전역 카운터 count 까지만 보이게 자른다. */
  const paintText = (rich, count) => {
    rich.parts.forEach((p) => {
      const take = Math.max(0, Math.min(p.full.length, count - p.start));
      const next = p.full.slice(0, take);
      if (p.node.nodeValue !== next) p.node.nodeValue = next;
    });
  };

  const setupVolty = (root) => {
    const tip = root.querySelector("[data-volty-tip]");
    const ask = root.querySelector("[data-volty-ask]");
    const answer = root.querySelector("[data-volty-answer]");
    const clip = root.querySelector("[data-volty-clip]");
    const chat = root.querySelector("[data-volty-chat]");
    if (!tip || !ask || !answer || !clip || !chat) return;

    const tipRich = collectText(tip);
    const askRich = collectText(ask);
    const ansRich = collectText(answer);

    /** 대화가 길어지면 바닥이 보이도록 위로 밀어 올린다. */
    const follow = () => {
      const depth = Math.max(0, chat.offsetHeight - clip.offsetHeight);
      chat.style.transform = `translateY(-${depth}px)`;
    };

    const done = () => {
      root.dataset.open = "1";
      root.dataset.tip = "typing";
      root.dataset.ask = "typing";
      root.dataset.phase = "chips";
      paintText(tipRich, tipRich.total);
      paintText(askRich, askRich.total);
      paintText(ansRich, ansRich.total);
      follow();
    };

    if (reduced()) {
      // 시트가 열린 완성 상태 — 첫 화면으로 멈추면 "Volty 가 설명해 준다"는 요지가 사라진다
      done();
      return;
    }

    let timers = [];
    let stream = 0;

    const clearAll = () => {
      timers.forEach(window.clearTimeout);
      timers = [];
      window.clearInterval(stream);
      stream = 0;
    };

    /** rich 를 흘리고 끝나면 next() 를 부른다. tick 마다 chars 글자씩 나아간다. */
    const type = (rich, next, onTick, ms, chars) => {
      let i = 0;
      const step = chars || 1;
      paintText(rich, 0);
      stream = window.setInterval(() => {
        i = Math.min(rich.total, i + step);
        paintText(rich, i);
        if (onTick) onTick();
        if (i >= rich.total) {
          window.clearInterval(stream);
          stream = 0;
          next();
        }
      }, ms || VOLTY_TIP_MS);
    };

    const at = (ms, fn) => timers.push(window.setTimeout(fn, ms));

    const reset = () => {
      clearAll();
      root.dataset.open = "0";
      root.dataset.tapping = "0";
      root.dataset.tip = "dots";
      root.dataset.ask = "dots";
      root.dataset.phase = "idle";
      paintText(tipRich, 0);
      paintText(askRich, 0);
      paintText(ansRich, 0);
      chat.style.transform = "translateY(0)";
    };

    const run = () => {
      reset();
      // ① 말풍선 — 점 로딩 뒤 타이핑
      at(1200, () => {
        root.dataset.tip = "typing";
        type(tipRich, () => {
          // ② 배지 탭 → 시트 오픈
          at(1500, () => {
            root.dataset.tapping = "1";
          });
          at(2000, () => {
            root.dataset.tapping = "0";
          });
          at(2200, () => {
            root.dataset.open = "1";
            // ③ 질문 타이핑 — 시트가 다 올라온 뒤에 시작한다
            at(1000, () => {
              root.dataset.ask = "typing";
              type(
                askRich,
                () => {
                  at(400, () => {
                    root.dataset.phase = "context";
                    follow();
                  });
                  at(1600, () => {
                    root.dataset.phase = "answer";
                    type(
                      ansRich,
                      () => {
                        at(500, () => {
                          root.dataset.phase = "chips";
                          follow();
                          // ④ 잠시 두었다가 닫고 처음부터
                          at(4000, () => {
                            root.dataset.open = "0";
                            at(900, run);
                          });
                        });
                      },
                      follow,
                      VOLTY_CHAT_MS,
                      VOLTY_CHAT_CHARS,
                    );
                  });
                },
                null,
                VOLTY_CHAT_MS,
                VOLTY_CHAT_CHARS,
              );
            });
          });
        });
      });
    };

    if (!("IntersectionObserver" in window)) {
      run();
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? run() : reset()),
      { threshold: 0.15 },
    );
    io.observe(root);
  };

  const KINDS = [
    ["[data-app-gaspass]", setupGasPass],
    ["[data-app-volty]", setupVolty],
  ];

  const init = () => {
    KINDS.forEach(([sel, setup]) => {
      document.querySelectorAll(sel).forEach((root) => {
        if (root.dataset.featureDemoReady === "1") return; // 멱등
        root.dataset.featureDemoReady = "1";
        setup(root);
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}
